import { useEffect, useRef, useState } from "react";
import "../styles/profileMenu.css";

type ProfileMenuProps = {
  orgName: string;
  orgSubtext?: string;
  signedInUser: {
    name: string;
    email: string;
    avatarUrl?: string;
  };
  activeAccountId?: string;

  onAccountSelect?: (accountId: string) => void;
  onSignOut?: () => void;
  onViewAccount?: () => void;
  onSwitchOrganization?: () => void;
  onSignInDifferent?: () => void;

  defaultOpen?: boolean;
};

export default function ProfileMenu({
  orgName,
  orgSubtext,
  signedInUser,
  onSignOut,
  onSignInDifferent,
  defaultOpen = false,
}: ProfileMenuProps) {
  const [open, setOpen] = useState(defaultOpen);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Click outside to close
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="msProfileWrap" ref={wrapRef}>
      {/* Trigger button (generic silhouette) */}
      <button
        className="msProfileTrigger msProfileTrigger--icon"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
      >
        <span className="msUserIcon" aria-hidden="true" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="msMenu" role="menu" aria-label="Profile menu">
          {/* Header row */}
          <div className="msMenuHeaderRow">
            <div className="msMenuHeaderLeft">
              <div className="msMenuHeaderTitle">{orgName}</div>
              {orgSubtext ? (
                <div className="msMenuHeaderSub">{orgSubtext}</div>
              ) : null}
            </div>
            <button
              className="msMenuLinkButton"
              type="button"
              onClick={onSignOut}
            >
              Sign out
            </button>
          </div>

          {/* Big profile card */}
          <div className="msBigCard">
            <div className="msBigCardAvatarWrap">
              {signedInUser.avatarUrl ? (
                <img
                  className="msBigAvatar"
                  src={signedInUser.avatarUrl}
                  alt=""
                />
              ) : (
                <div
                  className="msBigAvatar msBigAvatar--iconFallback"
                  aria-hidden="true"
                >
                  <span className="msUserIcon msUserIcon--big" />
                </div>
              )}
            </div>

            <div className="msBigCardInfo">
              <div className="msBigName" title={signedInUser.name}>
                {signedInUser.name}
              </div>
              <div className="msBigEmail" title={signedInUser.email}>
                {signedInUser.email}
              </div>
            </div>
          </div>

          <div className="msAccounts">
            <button
              className="msAccountRow msAccountRow--secondary"
              type="button"
              onClick={onSignInDifferent}
            >
              <div className="msAccountIcon">
                <div
                  className="msAccountAvatar msAccountAvatar--secondary"
                  aria-hidden="true"
                >
                  <span className="msUserIcon msUserIcon--small" />
                </div>
              </div>
              <div className="msAccountText">
                <div className="msAccountName">
                  Sign in with a different account
                </div>
              </div>
              <div className="msCheckSpacer" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
